import { forwardRef } from "react"
import { cn } from "@/lib/utils"

import {
  Select as ShadSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select"

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps {
  options: SelectOption[]
  label?: string
  error?: string
  hint?: string
  size?: "sm" | "md" | "lg"
  variant?: "default" | "ghost" | "glass"
  fullWidth?: boolean
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  className?: string
  id?: string
  placeholder?: string;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      label,
      error,
      hint,
      size = "md",
      variant = "default",
      fullWidth = false,
      disabled,
      className,
      value,
      defaultValue,
      onValueChange,
      id,
      placeholder = "Select user...",
    },
    ref
  ) => {
    const sizeClasses = {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-3 text-sm",
      lg: "h-12 px-4 text-base",
    }

    const variantClasses = {
      default: cn(
        "bg-white dark:bg-surface-900/50",
        "border border-surface-200 dark:border-surface-700",
        "hover:border-surface-300 dark:hover:border-surface-600",
        "focus-visible:ring-2 focus-visible:ring-primary-500/20"
      ),
      ghost: cn(
        "bg-transparent border border-transparent",
        "hover:bg-surface-100 dark:hover:bg-surface-800/50",
        "focus-visible:ring-2 focus-visible:ring-primary-500/20"
      ),
      glass: cn(
        "bg-surface-100/80 dark:bg-surface-800/50 backdrop-blur-sm",
        "border border-surface-200 dark:border-surface-700",
        "hover:border-surface-300 dark:hover:border-surface-600",
        "focus-visible:ring-2 focus-visible:ring-primary-500/30"
      ),
    }

    return (
      <div className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}>
        {label && (
          <label className="text-xs font-medium text-surface-600 dark:text-surface-400">
            {label}
          </label>
        )}

        <ShadSelect
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            id={id}
            ref={ref}
            className={cn(
              "relative w-full rounded-xl",
              "text-surface-900 dark:text-surface-100",
              "transition-all duration-200",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "data-[placeholder]:text-surface-400",
                "dark:data-[placeholder]:text-surface-500",
              sizeClasses[size],
              variantClasses[variant],
              error &&
                "border-danger-500 focus-visible:ring-danger-500/20",
              className
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>

          <SelectContent
            className={cn(
              "rounded-xl border border-surface-200 dark:border-surface-700",
              "bg-white dark:bg-surface-800"
            )}
          >
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  "cursor-pointer",
                  "text-surface-900 dark:text-surface-100",
                  "focus:bg-surface-100 dark:focus:bg-surface-700"
                )}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </ShadSelect>

        {hint && !error && (
          <p className="text-xs text-surface-500 dark:text-surface-400">
            {hint}
          </p>
        )}

        {error && <p className="text-xs text-danger-500">{error}</p>}
      </div>
    )
  }
)

Select.displayName = "Select"
